<template>
  <div class="pp-root" :dir="lang === 'he' ? 'rtl' : 'ltr'">

    <!-- Language switcher -->
    <div class="pp-lang-bar">
      <button v-for="l in langs" :key="l.code"
        class="pp-lang-btn" :class="{ active: lang === l.code }"
        @click="lang = l.code">
        {{ l.short }}
      </button>
    </div>

    <!-- Hero -->
    <div class="pp-hero">
      <div class="pp-hero-inner">
        <div class="pp-logo">🗺️ Eifoany</div>
        <h1 class="pp-hero-title">{{ tr.heroTitle }}</h1>
        <p class="pp-hero-sub">{{ tr.heroSub }}</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="pp-center">
      <div class="pp-spinner"></div>
    </div>

    <!-- Plans grid -->
    <div v-else class="pp-plans-wrap">
      <div class="pp-plans-grid">
        <div v-for="planKey in planKeys" :key="planKey"
          class="pp-plan-card" :class="{ 'pp-plan-card--featured': planKey === 'starter' }">

          <div v-if="planKey === 'starter'" class="pp-badge-popular">{{ tr.popular }}</div>

          <!-- Plan title & price -->
          <div class="pp-plan-top" :class="'pp-accent-' + planKey">
            <div class="pp-plan-name">{{ plans[planKey].icon }} {{ plans[planKey].label }}</div>
            <div class="pp-plan-price">
              <span v-if="plans[planKey].price === 0" class="pp-price-free">{{ tr.free }}</span>
              <span v-else>
                <span class="pp-price-amount">{{ plans[planKey].price }}₪</span>
                <span class="pp-price-period">{{ tr.perMonth }}</span>
              </span>
            </div>
          </div>

          <!-- Limits -->
          <div class="pp-limits">
            <div class="pp-limit-row">
              <span class="pp-limit-icon">🎮</span>
              <span>
                <strong>{{ plans[planKey].maxGames >= 999 ? tr.unlimited : plans[planKey].maxGames }}</strong>
                {{ tr.routes }}
              </span>
            </div>
            <div class="pp-limit-row">
              <span class="pp-limit-icon">📍</span>
              <span>
                <strong>{{ plans[planKey].maxPoints >= 999 ? tr.unlimited : plans[planKey].maxPoints }}</strong>
                {{ tr.pointsPerRoute }}
              </span>
            </div>
          </div>

          <!-- Feature categories -->
          <div v-for="cat in featureCategories" :key="cat.key" class="pp-cat">
            <div class="pp-cat-label">{{ cat.labels[lang] }}</div>
            <div v-for="feat in cat.features" :key="feat.key" class="pp-feat-row"
              :class="{ 'pp-feat-row--off': !plans[planKey].features[feat.key] }">
              <span class="pp-feat-icon">{{ plans[planKey].features[feat.key] ? '✅' : '✗' }}</span>
              <span class="pp-feat-name">{{ feat.labels[lang] }}</span>
            </div>
          </div>

          <!-- CTA -->
          <div class="pp-cta-wrap">
            <button @click="startCheckout(planKey)"
              class="pp-cta-btn" :class="'pp-cta-' + planKey"
              :disabled="checkingOut === planKey">
              <span v-if="checkingOut === planKey">⏳</span>
              <span v-else>{{ plans[planKey].price === 0 ? tr.startFree : tr.choosePlan }}</span>
            </button>
          </div>

        </div>
      </div>

      <!-- Notes -->
      <div class="pp-notes">
        <div class="pp-note">
          <span>📹</span><span>{{ tr.noteVideo }}</span>
        </div>
        <div class="pp-note">
          <span>🔒</span><span>{{ tr.noteData }}</span>
        </div>
        <div class="pp-note">
          <span>⬆️</span><span>{{ tr.noteUpgrade }}</span>
        </div>
      </div>
    </div>

    <!-- Checkout error (toujours visible, au-dessus du footer) -->
    <div v-if="checkoutError" class="pp-checkout-error">
      ⚠️ {{ checkoutError }}
      <button @click="checkoutError = null" class="pp-error-close">✕</button>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import firebase from 'firebase/app';
import 'firebase/database';
import 'firebase/auth';
import { PLAN_KEYS, FEATURE_CATEGORIES, DEFAULT_PLANS } from '../plans.js';

const PAYMENT_LINKS = {
  starter: 'https://buy.stripe.com/test_eVq4gBgAy43CgXL7m7aAw00',
  pro:     'https://buy.stripe.com/test_00w3cxdomgQofTHeOzaAw01',
};

const TRANSLATIONS = {
  fr: {
    heroTitle:    'Créez vos propres parcours de navigation',
    heroSub:      'Choisissez le plan adapté à votre projet. Pas de carte de crédit requise pour commencer.',
    popular:      '⭐ Le plus populaire',
    free:         'Gratuit',
    perMonth:     '/mois',
    unlimited:    'Illimité',
    routes:       'parcours',
    pointsPerRoute: 'points par parcours',
    startFree:    'Commencer gratuitement',
    choosePlan:   'Choisir ce plan',
    noteVideo:    'Vidéo : intégrez vos vidéos YouTube — collez simplement l\'URL, pas d\'upload requis.',
    noteData:     'Données sécurisées : vos parcours sont stockés sur Firebase et accessibles uniquement à vous.',
    noteUpgrade:  'Mise à niveau : changez de plan à tout moment depuis votre tableau de bord.',
  },
  en: {
    heroTitle:    'Create your own navigation routes',
    heroSub:      'Choose the plan that fits your project. No credit card required to get started.',
    popular:      '⭐ Most popular',
    free:         'Free',
    perMonth:     '/month',
    unlimited:    'Unlimited',
    routes:       'routes',
    pointsPerRoute: 'points per route',
    startFree:    'Start for free',
    choosePlan:   'Choose this plan',
    noteVideo:    'Video: embed your YouTube videos — just paste the URL, no upload needed.',
    noteData:     'Secure data: your routes are stored on Firebase and only accessible to you.',
    noteUpgrade:  'Upgrade: change your plan at any time from your dashboard.',
  },
  he: {
    heroTitle:    'צרו מסלולי ניווט משלכם',
    heroSub:      'בחרו את התוכנית המתאימה לפרויקט שלכם. לא נדרש כרטיס אשראי כדי להתחיל.',
    popular:      '⭐ הפופולרי ביותר',
    free:         'חינמי',
    perMonth:     '/חודש',
    unlimited:    'ללא הגבלה',
    routes:       'מסלולים',
    pointsPerRoute: 'נקודות למסלול',
    startFree:    'התחל בחינם',
    choosePlan:   'בחר תוכנית זו',
    noteVideo:    'וידאו: שלבו סרטוני YouTube — פשוט הדביקו את הקישור, ללא צורך בהעלאה.',
    noteData:     'נתונים מאובטחים: המסלולים שלכם מאוחסנים ב-Firebase ונגישים רק לכם.',
    noteUpgrade:  'שדרוג: שנו תוכנית בכל עת מלוח הבקרה שלכם.',
  },
};

export default {
  data() {
    return {
      lang: localStorage.getItem('adminLang') || 'fr',
      langs: [
        { code: 'fr', short: 'FR' },
        { code: 'en', short: 'EN' },
        { code: 'he', short: 'עב' },
      ],
      loading: true,
      checkingOut: null,
      checkoutError: null,
      plans: {},
      planKeys: PLAN_KEYS,
      featureCategories: FEATURE_CATEGORIES,
    };
  },

  computed: {
    tr() {
      return TRANSLATIONS[this.lang] || TRANSLATIONS.fr;
    },
  },

  mounted() {
    firebase.database().ref('/plans').on('value', snap => {
      const stored = snap.val() || {};
      const merged = {};
      for (const key of PLAN_KEYS) {
        const def = JSON.parse(JSON.stringify(DEFAULT_PLANS[key]));
        const src = stored[key] || {};
        merged[key] = {
          label:     src.label     ?? def.label,
          icon:      src.icon      ?? def.icon,
          price:     src.price     ?? def.price,
          maxGames:  src.maxGames  ?? def.maxGames,
          maxPoints: src.maxPoints ?? def.maxPoints,
          features:  { ...def.features, ...(src.features || {}) },
        };
      }
      this.plans = merged;
      this.loading = false;
    });
  },

  beforeUnmount() {
    firebase.database().ref('/plans').off();
  },

  methods: {
    getUser() {
      return new Promise(resolve => {
        const user = firebase.auth().currentUser;
        if (user) { resolve(user); return; }
        const unsub = firebase.auth().onAuthStateChanged(u => { unsub(); resolve(u); });
      });
    },

    async startCheckout(planKey) {
      this.checkoutError = null;
      if (planKey === 'free') {
        this.$router.push('/dashboard');
        return;
      }
      this.checkingOut = planKey;
      const user = await this.getUser();
      if (!user) {
        this.checkingOut = null;
        this.$router.push('/signin');
        return;
      }
      window.location.href = PAYMENT_LINKS[planKey];
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }

.pp-root {
  min-height: 100vh;
  background: #f8fafc;
  font-family: system-ui, sans-serif;
}

/* ── Lang bar ── */
.pp-lang-bar {
  display: flex; justify-content: flex-end; gap: 4px;
  padding: 8px 16px;
  background: white;
  border-bottom: 1px solid #f1f5f9;
}
.pp-lang-btn {
  padding: 4px 10px; border-radius: 6px; border: 1.5px solid #ddd;
  background: transparent; font-size: 13px; font-weight: 600; color: #555;
  cursor: pointer; transition: all 0.15s;
}
.pp-lang-btn.active { background: #1a73e8; border-color: #1a73e8; color: white; }
.pp-lang-btn:hover:not(.active) { background: #f1f5f9; }

/* ── Hero ── */
.pp-checkout-error {
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
  max-width: 560px; width: calc(100% - 48px);
  padding: 14px 18px;
  background: #fef2f2; border: 1.5px solid #fca5a5; border-radius: 12px;
  color: #991b1b; font-size: 14px; font-weight: 500;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15); z-index: 999;
}
.pp-error-close {
  background: none; border: none; cursor: pointer;
  font-size: 18px; color: #991b1b; flex-shrink: 0; line-height: 1;
}

.pp-hero {
  background: linear-gradient(135deg, #1a73e8 0%, #7c3aed 100%);
  padding: 60px 24px 48px;
  text-align: center; color: white;
}
.pp-hero-inner { max-width: 640px; margin: 0 auto; }
.pp-logo { font-size: 32px; margin-bottom: 16px; }
.pp-hero-title { font-size: 32px; font-weight: 800; margin: 0 0 12px; line-height: 1.2; }
.pp-hero-sub { font-size: 16px; opacity: 0.85; margin: 0; }

/* ── Loading ── */
.pp-center { display: flex; align-items: center; justify-content: center; padding: 80px; }
.pp-spinner {
  width: 40px; height: 40px;
  border: 4px solid #e2e8f0; border-top-color: #1a73e8;
  border-radius: 50%; animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Plans ── */
.pp-plans-wrap { max-width: 1060px; margin: 0 auto; padding: 40px 20px 60px; }

.pp-plans-grid {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 20px; align-items: start;
}
@media (max-width: 820px) { .pp-plans-grid { grid-template-columns: 1fr; } }

.pp-plan-card {
  background: white; border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  overflow: hidden; position: relative;
}
.pp-plan-card--featured {
  box-shadow: 0 6px 32px rgba(26,115,232,0.2);
  border: 2px solid #1a73e8;
}
.pp-badge-popular {
  position: absolute; top: -1px; right: 16px;
  background: #1a73e8; color: white;
  font-size: 11px; font-weight: 700;
  padding: 4px 12px; border-radius: 0 0 8px 8px;
}

/* ── Plan top ── */
.pp-plan-top { padding: 24px 20px 20px; color: white; min-height: 148px; }
.pp-accent-free    { background: #64748b; }
.pp-accent-starter { background: #1a73e8; }
.pp-accent-pro     { background: #7c3aed; }

.pp-plan-name { font-size: 20px; font-weight: 800; margin-bottom: 8px; }
.pp-price-free { font-size: 28px; font-weight: 800; }
.pp-price-amount { font-size: 36px; font-weight: 800; }
.pp-price-period { font-size: 14px; opacity: 0.8; margin-inline-start: 2px; }

/* ── Limits ── */
.pp-limits { padding: 14px 20px; border-bottom: 1px solid #f1f5f9; }
.pp-limit-row {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: #475569; padding: 4px 0;
}
.pp-limit-icon { font-size: 16px; }

/* ── Features ── */
.pp-cat { padding: 12px 20px; border-bottom: 1px solid #f1f5f9; }
.pp-cat-label {
  font-size: 10px; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 8px;
}
.pp-feat-row {
  display: flex; align-items: center; gap: 8px;
  padding: 5px 0; font-size: 13px; color: #334155;
}
.pp-feat-row--off { opacity: 0.35; }
.pp-feat-icon { font-size: 14px; width: 20px; text-align: center; flex-shrink: 0; }
.pp-feat-name { font-weight: 500; }

/* ── CTA ── */
.pp-cta-wrap { padding: 20px; }
.pp-cta-btn {
  display: block; width: 100%; padding: 13px;
  border-radius: 10px; font-size: 15px; font-weight: 700;
  text-align: center; text-decoration: none; transition: opacity 0.15s;
  border: none; cursor: pointer;
}
.pp-cta-btn:hover:not(:disabled) { opacity: 0.87; }
.pp-cta-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pp-cta-free    { background: #f1f5f9; color: #475569; }
.pp-cta-starter { background: #1a73e8; color: white; }
.pp-cta-pro     { background: #7c3aed; color: white; }

/* ── Notes ── */
.pp-notes { margin-top: 40px; display: flex; flex-direction: column; gap: 12px; }
.pp-note {
  display: flex; align-items: flex-start; gap: 10px;
  background: white; border-radius: 10px; padding: 14px 16px;
  font-size: 13px; color: #475569; line-height: 1.5;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}
.pp-note span:first-child { font-size: 18px; flex-shrink: 0; }
</style>
